import { useState, type FormEvent, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@nanostores/react';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { apiRequestMagicLink, apiVerifyMagicCode, apiGoogleSignIn } from '~/lib/api/auth';
import { renderGoogleButton, getGoogleClientId } from '~/lib/google-identity';
import { setAuth } from '~/lib/stores/auth';
import { loadProviderList } from '~/lib/provider-list';
import { initializePreferencesFromServer } from '~/lib/stores/settings';
import { isStayOnPagePath, $showAuthModal, $pendingMessage } from '~/lib/stores/authModal';
import { addExceptionStep } from '~/lib/posthog';
import { clearLandingAutostart } from '~/lib/attribution';
import { fireSignupConversion } from '~/lib/conversion';
import { useIsTouch } from '~/lib/hooks/useMediaQuery';

interface AuthModalProps {
  onAuthenticated?: () => void;
}

export default function AuthModal({ onAuthenticated }: AuthModalProps = {}) {
  const show = useStore($showAuthModal);
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const googleRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // Autofocus pops the on-screen keyboard over half the modal before the user picks Google vs email.
  const isTouch = useIsTouch();

  useEffect(() => {
    if (show) {
      setStep('email');
      setEmail('');
      setCode('');
      setLoading(false);
    }
  }, [show]);

  function finishAuth(accessToken: string, user: Parameters<typeof setAuth>[1], isNewUser?: boolean) {
    // Read before setAuth — flipping auth true is what makes Chat flush the
    // pending prompt, and we need to know whether that is about to happen.
    const hasPendingMessage = $pendingMessage.get() != null;

    setAuth(accessToken, user);
    loadProviderList().catch(console.error);
    initializePreferencesFromServer().catch(console.error);
    $showAuthModal.set(false);
    onAuthenticated?.();

    if (!isNewUser) {
      return;
    }

    /*
     * New account. The conversion has to fire in the browser either way, but how
     * we get there depends on what the user was doing:
     *
     * - Signed up with a prompt queued (the path that matters most): Chat is
     *   listening for the auth false -> true flip to send it. Navigating now
     *   would unmount Chat mid-flush and lose the build, so we stay put and fire
     *   inline instead.
     * - Signed up cold: nothing in flight, so send them to /welcome, which gives
     *   the ad platforms a distinct URL and fires the pixel there.
     *
     * fireSignupConversion is idempotent, so the two paths can't double-count.
     */
    // A handoff page (desktop sign-in) has to stay put, or its redirect is lost.
    if (hasPendingMessage || isStayOnPagePath(window.location.pathname)) {
      fireSignupConversion();
    } else {
      navigate('/welcome');
    }
  }

  // Render the Google button when on the email step (no-ops without VITE_GOOGLE_CLIENT_ID)
  useEffect(() => {
    if (!show || step !== 'email' || !googleRef.current) {
      return;
    }

    renderGoogleButton(googleRef.current, async (idToken) => {
      try {
        const { accessToken, user, isNewUser } = await apiGoogleSignIn(idToken);
        finishAuth(accessToken, user, isNewUser);
      } catch (err: any) {
        toast.error(err?.response?.data?.message || 'Google sign-in failed');
      }
    }).catch(console.error);
  }, [show, step]);

  function close() {
    $showAuthModal.set(false);
    $pendingMessage.set(null);

    // Dismissed before asking for a link: the landing prompt shouldn't reopen
    // this modal on every visit. After a link was sent, keep it — the link's
    // new tab is where the build starts.
    if (step === 'email') {
      clearLandingAutostart();
    }
  }

  async function handleEmailSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    addExceptionStep('magic_link_requested', {});

    try {
      await apiRequestMagicLink(email);
      setStep('code');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Could not send code');
    } finally {
      setLoading(false);
    }
  }

  async function handleCodeSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);

    try {
      const { accessToken, user, isNewUser } = await apiVerifyMagicCode(email, code);
      finishAuth(accessToken, user, isNewUser);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Invalid code');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="ui-root ui-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={close}
        >
          <div
            className="ui-modal"
            style={{ width: 420, maxWidth: 'calc(100vw - 24px)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="ui-btn-icon absolute right-3 top-3 z-2 touch:right-1.5 touch:top-1.5"
            >
              <span className="i-ph:x" style={{ fontSize: 16 }} />
            </button>

            <div className="px-5 pb-5 pt-7 text-center sm:px-7">
              <div className="inline-flex items-center justify-center" style={{ gap: 8 }}>
                <img src="/dunea-mark.svg" width={24} height={24} alt="" style={{ display: 'block' }} />
                <span className="ui-brand" style={{ fontSize: 20, lineHeight: 1, color: 'var(--color-primary)' }}>
                  Dunea
                </span>
              </div>
              <h2 className="ui-serif" style={{ fontSize: 30, margin: '18px 0 6px', lineHeight: 1.1, fontWeight: 400 }}>
                {step === 'email' ? 'Welcome to Dunea.' : 'Check your email.'}
              </h2>
              <p style={{ margin: 0, color: 'var(--color-secondary)', fontSize: 13.5 }}>
                {step === 'email' ? 'Sign in or create your account.' : `We sent a 6-digit code to ${email}.`}
              </p>
            </div>

            <div className="px-5 pb-7 sm:px-7">
              {step === 'email' ? (
                <>
                  {getGoogleClientId() && (
                    <>
                      <div ref={googleRef} style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }} />
                      <div
                        className="ui-or"
                        style={{ textAlign: 'center', color: 'var(--color-muted)', fontSize: 12, margin: '4px 0 12px' }}
                      >
                        or
                      </div>
                    </>
                  )}
                  <form onSubmit={handleEmailSubmit}>
                    <label className="ui-label" htmlFor="auth-email">
                      Email
                    </label>
                    <input
                      id="auth-email"
                      className="ui-input"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@studio.com"
                      autoComplete="email"
                      autoFocus={!isTouch}
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      className="ui-btn-accent"
                      style={{ width: '100%', padding: '11px 16px', marginTop: 16 }}
                    >
                      {loading ? 'Sending code...' : 'Send code'}
                    </button>
                  </form>
                </>
              ) : (
                <form onSubmit={handleCodeSubmit}>
                  <label className="ui-label" htmlFor="auth-code">
                    6-digit code
                  </label>
                  <input
                    id="auth-code"
                    className="ui-input"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                    autoComplete="one-time-code"
                    autoFocus
                    style={{ letterSpacing: 6, textAlign: 'center', fontFamily: 'monospace' }}
                  />
                  <button
                    type="submit"
                    disabled={loading || code.length !== 6}
                    className="ui-btn-accent"
                    style={{ width: '100%', padding: '11px 16px', marginTop: 16 }}
                  >
                    {loading ? 'Verifying...' : 'Verify & sign in'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStep('email');
                      setCode('');
                    }}
                    className="ui-btn-ghost"
                    style={{ width: '100%', marginTop: 10, fontSize: 12.5 }}
                  >
                    Use a different email
                  </button>
                </form>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
